<template>
    <Card pageTitle="Loyalty Campaigns">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_LOYALTY">
            <div class="campaigns-wrap">
                <div class="page-head">
                    <div>
                        <h2>Loyalty Campaigns</h2>
                        <p>Draft visit bonuses, group point grants, and customer-bound digital vouchers. Approval and issuance are separate actions.</p>
                    </div>
                    <div class="head-actions">
                        <span :class="['status-badge', programEnabled ? 'ok' : 'muted']">
                            Program {{ programEnabled ? "enabled" : "disabled" }}
                        </span>
                        <Button borderOnly noMargin @click="loadPage" :disabled="loading">
                            {{ loading ? "Loading..." : "Refresh" }}
                        </Button>
                    </div>
                </div>

                <section v-if="canEdit" class="panel create-panel">
                    <header>
                        <h3>New Campaign</h3>
                        <span class="muted">Draft until approved</span>
                    </header>
                    <div class="form-grid">
                        <label class="field">
                            <span>Name</span>
                            <input v-model.trim="form.name" class="native-input" type="text" />
                        </label>
                        <label class="field">
                            <span>Type</span>
                            <select v-model="form.type" class="native-input" @change="changeCampaignType(form)">
                                <option value="VISIT_BONUS">Visit bonus</option>
                                <option value="GROUP_BONUS">Group bonus points</option>
                                <option value="DIGITAL_VOUCHER">Digital voucher</option>
                            </select>
                        </label>
                        <label v-if="form.type !== 'DIGITAL_VOUCHER'" class="field small">
                            <span>Points</span>
                            <input v-model.number="form.points_awarded" min="1" step="1" class="native-input" type="number" />
                        </label>
                        <label class="field">
                            <span>Starts (America/Toronto)</span>
                            <input v-model="form.start_at" class="native-input" type="datetime-local" />
                        </label>
                        <label class="field">
                            <span>Ends (America/Toronto)</span>
                            <input v-model="form.end_at" class="native-input" type="datetime-local" />
                        </label>
                        <label v-if="form.type === 'VISIT_BONUS'" class="field small">
                            <span>Per customer</span>
                            <input v-model="form.per_customer_limit" min="1" step="1" class="native-input" type="number" />
                        </label>
                        <label class="field small">
                            <span>Total grants</span>
                            <input v-model="form.total_grant_limit" min="1" step="1" class="native-input" type="number" />
                        </label>
                    </div>
                    <label class="field">
                        <span>Description</span>
                        <textarea v-model.trim="form.description" class="native-input text-area" rows="2"></textarea>
                    </label>
                    <CampaignTermsFields v-model="form" :groups="groups" :disabled="saving" new-policy />
                    <div class="actions">
                        <Button noMargin @click="createCampaign" :disabled="saving">{{ saving ? "Creating..." : "Create draft" }}</Button>
                    </div>
                </section>

                <div class="summary-row">
                    <div>
                        <span class="summary-label">Total</span>
                        <strong>{{ campaigns.length }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Active</span>
                        <strong>{{ activeCount }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Draft</span>
                        <strong>{{ draftCount }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Points granted</span>
                        <strong>{{ totalPointsGranted }}</strong>
                    </div>
                </div>

                <section class="panel">
                    <header>
                        <h3>Configured Campaigns</h3>
                        <span class="muted">Approval, schedule, grant limits, and audit trail</span>
                    </header>

                    <div v-if="!campaigns.length && !loading" class="empty-state">
                        No loyalty campaigns configured.
                    </div>

                    <div v-else class="campaign-list">
                        <article v-for="campaign in campaigns" :key="campaign.id" class="campaign-row">
                            <div class="campaign-main">
                                <div class="title-line">
                                    <input
                                        v-model.trim="campaign.edit.name"
                                        class="native-input title-input"
                                        type="text"
                                        :disabled="!canEdit || campaign.busy"
                                    />
                                    <span :class="['status-badge', runtimeClass(campaign.runtime_status)]">
                                        {{ runtimeLabel(campaign.runtime_status) }}
                                    </span>
                                    <span :class="['status-badge', campaign.approval_status === 'APPROVED' ? 'ok' : 'draft']">
                                        {{ campaign.approval_status === "APPROVED" ? "Approved" : "Draft" }}
                                    </span>
                                </div>

                                <div class="edit-grid">
                                    <label class="field">
                                        <span>Type</span>
                                        <select v-model="campaign.edit.type" class="native-input" :disabled="!canEdit || campaign.busy" @change="changeCampaignType(campaign.edit)">
                                            <option value="VISIT_BONUS">Visit bonus</option>
                                            <option value="GROUP_BONUS">Group bonus points</option>
                                            <option value="DIGITAL_VOUCHER">Digital voucher</option>
                                        </select>
                                    </label>
                                    <label v-if="campaign.edit.type !== 'DIGITAL_VOUCHER'" class="field small">
                                        <span>Points</span>
                                        <input
                                            v-model.number="campaign.edit.points_awarded"
                                            min="1"
                                            step="1"
                                            class="native-input"
                                            type="number"
                                            :disabled="!canEdit || campaign.busy"
                                        />
                                    </label>
                                    <label class="field">
                                        <span>Starts (America/Toronto)</span>
                                        <input
                                            v-model="campaign.edit.start_at"
                                            class="native-input"
                                            type="datetime-local"
                                            :disabled="!canEdit || campaign.busy"
                                        />
                                    </label>
                                    <label class="field">
                                        <span>Ends (America/Toronto)</span>
                                        <input
                                            v-model="campaign.edit.end_at"
                                            class="native-input"
                                            type="datetime-local"
                                            :disabled="!canEdit || campaign.busy"
                                        />
                                    </label>
                                    <label v-if="campaign.edit.type === 'VISIT_BONUS'" class="field small">
                                        <span>Per customer</span>
                                        <input
                                            v-model="campaign.edit.per_customer_limit"
                                            min="1"
                                            step="1"
                                            class="native-input"
                                            type="number"
                                            :disabled="!canEdit || campaign.busy"
                                        />
                                    </label>
                                    <label class="field small">
                                        <span>Total grants</span>
                                        <input
                                            v-model="campaign.edit.total_grant_limit"
                                            min="1"
                                            step="1"
                                            class="native-input"
                                            type="number"
                                            :disabled="!canEdit || campaign.busy"
                                        />
                                    </label>
                                </div>

                                <label class="field">
                                    <span>Description</span>
                                    <textarea
                                        v-model.trim="campaign.edit.description"
                                        class="native-input text-area"
                                        rows="2"
                                        :disabled="!canEdit || campaign.busy"
                                    ></textarea>
                                </label>

                                <CampaignTermsFields v-model="campaign.edit" :groups="groups" :disabled="!canEdit || campaign.busy" />
                                <p class="muted">Saving changed terms resets approval and disables this campaign. Preview and issue only the saved terms.</p>

                                <div class="meta-grid">
                                    <span>Grants: {{ campaign.grant_count || 0 }}</span>
                                    <span>Points granted: {{ campaign.points_granted || 0 }}</span>
                                    <span>Approved by: {{ campaign.approved_by_name || "-" }}</span>
                                    <span>Approved at: {{ formatDate(campaign.approved_at) }}</span>
                                </div>

                                <div v-if="campaign.approval_note" class="approval-note">
                                    <strong>Approval note</strong>
                                    <span>{{ campaign.approval_note }}</span>
                                </div>

                                <div v-if="latestHistory(campaign)" class="history-line">
                                    Last audit: {{ latestHistory(campaign).action }} by
                                    {{ latestHistory(campaign).user_name || "Unknown" }} on {{ formatDate(latestHistory(campaign).at) }}
                                </div>
                            </div>

                            <aside class="campaign-actions">
                                <Button
                                    v-if="canEdit"
                                    noMargin
                                    borderOnly
                                    @click="saveCampaign(campaign)"
                                    :disabled="campaign.busy"
                                >
                                    Save
                                </Button>
                                <div v-if="canEdit" class="approval-box">
                                    <textarea
                                        v-model.trim="approvalNotes[campaign.id]"
                                        class="native-input text-area"
                                        rows="2"
                                        placeholder="Approval note"
                                    ></textarea>
                                    <Button noMargin @click="approveCampaign(campaign)" :disabled="campaign.busy">
                                        Approve & enable
                                    </Button>
                                </div>
                                <Button
                                    v-if="canEdit && campaign.enabled"
                                    noMargin
                                    borderOnly
                                    variant="danger"
                                    @click="deactivateCampaign(campaign)"
                                    :disabled="campaign.busy"
                                >
                                    Deactivate
                                </Button>
                                <div v-if="canEdit && campaign.type === 'VISIT_BONUS'" class="manual-box">
                                    <strong>Manual grant</strong>
                                    <input
                                        v-model.trim="manualGrants[campaign.id].customer_id"
                                        class="native-input"
                                        type="text"
                                        placeholder="Customer ID"
                                    />
                                    <input
                                        v-model.trim="manualGrants[campaign.id].reason"
                                        class="native-input"
                                        type="text"
                                        placeholder="Reason"
                                    />
                                    <Button
                                        noMargin
                                        borderOnly
                                        @click="grantCampaign(campaign)"
                                        :disabled="campaign.busy || campaign.runtime_status !== 'ACTIVE' || !programEnabled"
                                    >
                                        Grant
                                    </Button>
                                </div>
                                <Button noMargin borderOnly @click="toggleGrants(campaign)" :disabled="campaign.grantsLoading">
                                    {{ campaign.showGrants ? "Hide grants" : "View grants" }}
                                </Button>
                                <div v-if="campaign.type !== 'VISIT_BONUS'" class="manual-box">
                                    <strong>Group issuance</strong>
                                    <Button v-if="canEdit" noMargin borderOnly @click="previewAudience(campaign)" :disabled="campaign.busy || hasUnsavedTerms(campaign)">Preview saved audience</Button>
                                    <p v-if="hasUnsavedTerms(campaign)" class="muted">Save changed terms before previewing or issuing.</p>
                                    <template v-if="campaign.snapshot">
                                        <p>{{ campaign.snapshot.recipient_count }} eligible · {{ campaign.snapshot.excluded_count }} excluded</p>
                                        <small>Snapshot {{ campaign.snapshot.id }} · {{ formatDate(campaign.snapshot.created_at) }}</small>
                                        <ul class="audience-list"><li v-for="recipient in campaign.snapshot.recipients" :key="recipient.customer_id">{{ recipient.customer_name || "Unnamed customer" }} · {{ recipient.customer_id }}</li></ul>
                                        <details v-if="campaign.snapshot.exclusions && campaign.snapshot.exclusions.length"><summary>Excluded customers</summary><p v-for="excluded in campaign.snapshot.exclusions" :key="excluded.customer_id">{{ excluded.customer_id }}: {{ excluded.reason }}</p></details>
                                        <label class="field"><span>Issuance reason</span><input v-model.trim="campaign.issueReason" class="native-input" :disabled="!canEdit || campaign.busy" /></label>
                                        <Button v-if="canEdit" noMargin @click="issueAudience(campaign)" :disabled="campaign.busy || hasUnsavedTerms(campaign) || campaign.runtime_status !== 'ACTIVE' || !programEnabled || !campaign.snapshot.recipient_count">Issue this snapshot</Button>
                                    </template>
                                    <p v-if="campaign.issueResult" role="status">Issued {{ campaign.issueResult.issued_count }} · skipped {{ campaign.issueResult.skipped_count }}</p>
                                    <Button noMargin borderOnly @click="loadIssuances(campaign)" :disabled="campaign.busy">View issuance history</Button>
                                    <details v-for="issuance in campaign.issuances" :key="issuance.id"><summary>{{ issuance.status || 'Snapshot' }} · {{ issuance.recipient_count }} recipients · {{ formatDate(issuance.created_at || issuance.createdAt) }}</summary><p>Snapshot {{ issuance.id }}</p><p v-for="result in issuance.results || []" :key="result.customer_id">{{ result.customer_id }}: {{ result.status }} {{ result.benefit_id || result.grant_id || '' }}</p></details>
                                </div>
                            </aside>

                            <div v-if="campaign.showGrants" class="grant-table-wrap">
                                <table class="grant-table">
                                    <thead>
                                        <tr>
                                            <th>Customer</th>
                                            <th>Points</th>
                                            <th>Invoice</th>
                                            <th>Reason</th>
                                            <th>When</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="grant in campaign.grants" :key="grant.id">
                                            <td>{{ grant.customer_id }}</td>
                                            <td>{{ grant.points_granted }}</td>
                                            <td>{{ grant.invoice_name || grant.invoice_id || "-" }}</td>
                                            <td>{{ grant.grant_reason || "-" }}</td>
                                            <td>{{ formatDate(grant.createdAt) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <p v-if="!campaign.grants.length && !campaign.grantsLoading" class="empty compact">
                                    No grants for this campaign.
                                </p>
                            </div>
                        </article>
                    </div>
                </section>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import CampaignTermsFields from "../../components/loyalty/CampaignTermsFields.vue"
import LoyaltyFetchApi from "../../utils/fetch/loyalty-fetch-api"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import { errMsg, confirmDialog } from "../../utils/common"
import { benefitPolicyEditor, benefitPolicyPayload, validateBenefitPolicy } from "../../utils/loyalty-benefit-policy"
import {
    businessDateTimeLocalFromInstant,
    businessDateTimeLocalToUtc,
    formatBusinessInstant,
} from "../../utils/business-date"

export default {
    components: { Card, Button, AllowPermission, CampaignTermsFields },
    data() {
        return {
            PERMISSION_CODES,
            loading: false,
            saving: false,
            campaigns: [],
            groups: [],
            approvalNotes: {},
            manualGrants: {},
            programEnabled: false,
            form: this.emptyForm(),
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        canEdit() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_LOYALTY,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        activeCount() {
            return this.campaigns.filter((campaign) => campaign.runtime_status === "ACTIVE").length
        },
        draftCount() {
            return this.campaigns.filter((campaign) => campaign.approval_status !== "APPROVED").length
        },
        totalPointsGranted() {
            return this.campaigns.reduce((sum, campaign) => sum + Number(campaign.points_granted || 0), 0)
        },
    },
    async mounted() {
        await this.loadPage()
    },
    methods: {
        changeCampaignType(edit) {
            edit.points_awarded = edit.type === "VISIT_BONUS" ? 1 : ""
        },
        async loadGroups() {
            try { this.groups = (await LoyaltyFetchApi.fetchCampaignGroups()) || [] }
            catch (err) { this.groups = []; this.$toast.error(errMsg(err, "Could not load audience groups")) }
        },
        hasUnsavedTerms(campaign) {
            return JSON.stringify(this.payloadFromEdit(campaign.edit)) !== JSON.stringify(this.payloadFromEdit(this.campaignEdit(campaign)))
        },
        async previewAudience(campaign) {
            if (!this.canEdit || campaign.busy || this.hasUnsavedTerms(campaign)) return
            campaign.busy = true
            campaign.snapshot = null
            campaign.issueResult = null
            try {
                const snapshot = await LoyaltyFetchApi.previewCampaignAudience(campaign.id)
                if (this.hasUnsavedTerms(campaign)) return
                campaign.snapshot = snapshot
                campaign.issueKey = `ADMIN_CAMPAIGN_ISSUE::${snapshot.id}`
            } catch (err) { this.$toast.error(errMsg(err, "Could not preview campaign audience")) }
            finally { campaign.busy = false }
        },
        async issueAudience(campaign) {
            if (!this.canEdit || campaign.busy || !campaign.snapshot || this.hasUnsavedTerms(campaign) || !this.programEnabled || campaign.runtime_status !== "ACTIVE") return
            const snapshot = campaign.snapshot
            const reason = String(campaign.issueReason || "").trim()
            if (!reason) { this.$toast.error("An issuance reason is required"); return }
            campaign.busy = true
            try {
                const confirmed = await confirmDialog({ title: "Issue campaign audience", text: `${campaign.name}: issue ${snapshot.recipient_count} customer-bound ${campaign.type === "DIGITAL_VOUCHER" ? "vouchers" : "point grants"} from snapshot ${snapshot.id}? Excluded customers will not receive an award.`, confirmButtonText: "Issue snapshot" })
                if (!confirmed || !this.canEdit || campaign.snapshot !== snapshot || this.hasUnsavedTerms(campaign)) return
                campaign.issueResult = await LoyaltyFetchApi.issueCampaignAudience(campaign.id, {
                    snapshot_id: snapshot.id, idempotency_key: campaign.issueKey, reason,
                })
                this.$toast.success(`Issued ${campaign.issueResult.issued_count}; skipped ${campaign.issueResult.skipped_count}`)
                try { campaign.issuances = (await LoyaltyFetchApi.fetchCampaignIssuances(campaign.id)) || [] }
                catch (err) { this.$toast.error(errMsg(err, "Issuance completed; could not refresh its history")) }
            } catch (err) { this.$toast.error(errMsg(err, "Could not issue campaign audience. Retry uses the same snapshot and request key.")) }
            finally { campaign.busy = false }
        },
        async loadIssuances(campaign) {
            if (campaign.busy) return
            campaign.busy = true
            try { campaign.issuances = (await LoyaltyFetchApi.fetchCampaignIssuances(campaign.id)) || [] }
            catch (err) { this.$toast.error(errMsg(err, "Could not load issuance history")) }
            finally { campaign.busy = false }
        },
        async loadPage() {
            this.loading = true
            try {
                const settings = await LoyaltyFetchApi.fetchSettings()
                this.programEnabled = !!settings?.enabled
                await Promise.all([this.loadCampaigns(false), this.loadGroups()])
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load loyalty campaigns"))
            }
            this.loading = false
        },
        emptyForm() {
            return {
                name: "",
                description: "",
                type: "VISIT_BONUS",
                points_awarded: 1,
                start_at: "",
                end_at: "",
                per_customer_limit: 1,
                total_grant_limit: "",
                group_ids: [], allow_campaign_accumulation: false,
                image: null, additional_images: [], image_alt: "",
                benefit_policy: benefitPolicyEditor(),
            }
        },
        campaignEdit(campaign) {
            return {
                name: campaign.name || "",
                description: campaign.description || "",
                type: campaign.type || "VISIT_BONUS",
                points_awarded: Number(campaign.points_awarded || 1),
                start_at: this.toDateTimeLocal(campaign.start_at),
                end_at: this.toDateTimeLocal(campaign.end_at),
                start_at_original: campaign.start_at || null,
                end_at_original: campaign.end_at || null,
                per_customer_limit: campaign.per_customer_limit || "",
                total_grant_limit: campaign.total_grant_limit || "",
                group_ids: [...(campaign.group_ids || [])],
                allow_campaign_accumulation: !!campaign.allow_campaign_accumulation,
                image: campaign.image || null, additional_images: [...(campaign.additional_images || [])], image_alt: campaign.image_alt || "",
                benefit_policy: benefitPolicyEditor(campaign.benefit_policy || {}),
            }
        },
        async loadCampaigns(ownLoading = true) {
            if (ownLoading) this.loading = true
            try {
                const campaigns = await LoyaltyFetchApi.fetchCampaigns()
                this.campaigns = campaigns.map((campaign) => ({
                    ...campaign,
                    edit: this.campaignEdit(campaign),
                    busy: false,
                    showGrants: false,
                    grantsLoading: false,
                    grants: [],
                    snapshot: null, issueReason: "", issueKey: "", issueResult: null, issuances: [],
                }))
                this.campaigns.forEach((campaign) => {
                    this.$set(this.approvalNotes, campaign.id, "")
                    this.$set(this.manualGrants, campaign.id, { customer_id: "", reason: "" })
                })
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load loyalty campaigns"))
            }
            if (ownLoading) this.loading = false
        },
        payloadFromEdit(edit) {
            return {
                name: edit.name,
                description: edit.description || null,
                type: edit.type || "VISIT_BONUS",
                points_awarded: edit.type === "DIGITAL_VOUCHER" ? 0 : Number(edit.points_awarded),
                start_at: this.fromDateTimeLocal(edit.start_at, edit.start_at_original),
                end_at: this.fromDateTimeLocal(edit.end_at, edit.end_at_original),
                per_customer_limit: edit.type === "VISIT_BONUS" ? (edit.per_customer_limit === "" ? null : Number(edit.per_customer_limit)) : 1,
                total_grant_limit: edit.total_grant_limit === "" ? null : Number(edit.total_grant_limit),
                group_ids: edit.type === "VISIT_BONUS" ? [] : [...edit.group_ids],
                allow_campaign_accumulation: !!edit.allow_campaign_accumulation,
                image: edit.image || null, additional_images: edit.additional_images || [], image_alt: edit.image_alt || "",
                benefit_policy: edit.type === "DIGITAL_VOUCHER" ? benefitPolicyPayload(edit.benefit_policy) : null,
            }
        },
        validatePayload(payload, edit = {}) {
            if (!payload.name) return "Campaign name is required"
            if (payload.type !== "DIGITAL_VOUCHER" && (!Number.isInteger(payload.points_awarded) || payload.points_awarded <= 0)) {
                return "Points must be greater than 0"
            }
            if (payload.type !== "VISIT_BONUS" && !payload.group_ids.length) return "Select at least one audience group"
            if (payload.image && !payload.image_alt.trim()) return "Describe the campaign artwork for accessibility"
            if (payload.benefit_policy) {
                const error = validateBenefitPolicy(payload.benefit_policy, true)
                if (error) return error
            }
            if (payload.per_customer_limit !== null && (!Number.isFinite(payload.per_customer_limit) || payload.per_customer_limit <= 0)) {
                return "Per-customer limit must be blank or greater than 0"
            }
            if (payload.total_grant_limit !== null && (!Number.isFinite(payload.total_grant_limit) || payload.total_grant_limit <= 0)) {
                return "Total grant limit must be blank or greater than 0"
            }
            if (edit.start_at && !payload.start_at) {
                return "Start time must be a valid, unambiguous America/Toronto time"
            }
            if (edit.end_at && !payload.end_at) {
                return "End time must be a valid, unambiguous America/Toronto time"
            }
            if (payload.start_at && payload.end_at && new Date(payload.end_at).getTime() <= new Date(payload.start_at).getTime()) {
                return "End date must be after start date"
            }
            return ""
        },
        async createCampaign() {
            if (!this.canEdit || this.saving) return
            const payload = this.payloadFromEdit(this.form)
            const error = this.validatePayload(payload, this.form)
            if (error) {
                this.$toast.error(error)
                return
            }
            this.saving = true
            try {
                await LoyaltyFetchApi.createCampaign(payload)
                this.form = this.emptyForm()
                this.$toast.success("Campaign draft created")
                await this.loadCampaigns()
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not create campaign"))
            }
            this.saving = false
        },
        async saveCampaign(campaign) {
            if (!this.canEdit || campaign.busy) return
            const payload = this.payloadFromEdit(campaign.edit)
            const error = this.validatePayload(payload, campaign.edit)
            if (error) {
                this.$toast.error(error)
                return
            }
            this.$set(campaign, "busy", true)
            try {
                await LoyaltyFetchApi.updateCampaign(campaign.id, payload)
                this.$toast.success("Campaign updated")
                await this.loadCampaigns()
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not update campaign"))
            }
            this.$set(campaign, "busy", false)
        },
        async approveCampaign(campaign) {
            if (!this.canEdit || campaign.busy) return
            if (this.hasUnsavedTerms(campaign)) { this.$toast.error("Save campaign terms before approval"); return }
            const note = String(this.approvalNotes[campaign.id] || "").trim()
            if (!note) {
                this.$toast.error("Approval note is required")
                return
            }
            this.$set(campaign, "busy", true)
            try {
                if (!await confirmDialog({ title: "Approve and enable campaign", text: `Enable ${campaign.name} with its saved terms? Issuance remains a separate action.`, confirmButtonText: "Approve and enable" })) return
                if (!this.canEdit || this.hasUnsavedTerms(campaign)) return
                await LoyaltyFetchApi.approveCampaign(campaign.id, { approval_note: note, enabled: true })
                this.$toast.success("Campaign approved and enabled")
                await this.loadCampaigns()
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not approve campaign"))
            }
            finally { this.$set(campaign, "busy", false) }
        },
        async deactivateCampaign(campaign) {
            if (!this.canEdit || campaign.busy) return
            this.$set(campaign, "busy", true)
            try {
                await LoyaltyFetchApi.deactivateCampaign(campaign.id, { note: "Deactivated from dashboard" })
                this.$toast.success("Campaign deactivated")
                await this.loadCampaigns()
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not deactivate campaign"))
            }
            this.$set(campaign, "busy", false)
        },
        async grantCampaign(campaign) {
            if (!this.canEdit || campaign.busy) return
            if (!this.programEnabled) {
                this.$toast.error("Enable the loyalty program before granting campaign points")
                return
            }
            const manual = this.manualGrants[campaign.id] || {}
            const customerId = String(manual.customer_id || "").trim()
            const reason = String(manual.reason || "").trim()
            if (!customerId || !reason) {
                this.$toast.error("Customer ID and reason are required")
                return
            }
            this.$set(campaign, "busy", true)
            try {
                if (!await confirmDialog({ title: "Grant campaign points", text: `${campaign.name}: grant ${campaign.points_awarded} points to Customer ID ${customerId}?`, confirmButtonText: "Grant points" }) || !this.canEdit) return
                await LoyaltyFetchApi.grantCampaignPoints(campaign.id, {
                    customer_id: customerId,
                    reason,
                    idempotency_key: `DASHBOARD_CAMPAIGN_GRANT::${campaign.id}::${customerId}::${Date.now()}`,
                })
                this.$set(this.manualGrants, campaign.id, { customer_id: "", reason: "" })
                this.$toast.success("Campaign points granted")
                await this.loadCampaigns()
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not grant campaign points"))
            }
            finally { this.$set(campaign, "busy", false) }
        },
        async toggleGrants(campaign) {
            if (campaign.showGrants) {
                this.$set(campaign, "showGrants", false)
                return
            }
            this.$set(campaign, "grantsLoading", true)
            try {
                const data = await LoyaltyFetchApi.fetchCampaignGrants(campaign.id, 1, 25)
                this.$set(campaign, "grants", data.result || [])
                this.$set(campaign, "showGrants", true)
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load campaign grants"))
            }
            this.$set(campaign, "grantsLoading", false)
        },
        latestHistory(campaign) {
            const history = campaign.audit_history || []
            return history.length ? history[history.length - 1] : null
        },
        runtimeLabel(status) {
            if (status === "ACTIVE") return "Active"
            if (status === "SCHEDULED") return "Scheduled"
            if (status === "ENDED") return "Ended"
            if (status === "DISABLED") return "Disabled"
            return "Draft"
        },
        runtimeClass(status) {
            if (status === "ACTIVE") return "ok"
            if (status === "SCHEDULED") return "info"
            if (status === "ENDED") return "ended"
            if (status === "DISABLED") return "muted"
            return "draft"
        },
        toDateTimeLocal(value) {
            return businessDateTimeLocalFromInstant(value)
        },
        fromDateTimeLocal(value, existingInstant = null) {
            return value ? businessDateTimeLocalToUtc(value, existingInstant) : null
        },
        formatDate(value) {
            if (!value) return "-"
            return formatBusinessInstant(value)
        },
    },
}
</script>

<style lang="scss" scoped>
.campaigns-wrap { max-width: 1240px; }
.page-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 18px; }
.head-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; justify-content: flex-end; }
.page-head h2 { margin: 0 0 6px; font-size: 24px; font-weight: 800; color: #0b302c; }
.page-head p { margin: 0; color: #5f6368; line-height: 1.45; }
.panel { border: 1px solid #e1e5e8; border-radius: 8px; padding: 14px; background: #fff; margin-bottom: 16px; }
.panel header { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin-bottom: 12px; }
.panel h3 { margin: 0; color: #111827; font-size: 17px; font-weight: 800; }
.muted { color: #6b7280; font-size: 13px; }
.form-grid, .edit-grid, .summary-row, .meta-grid { display: grid; gap: 12px; }
.form-grid { grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); align-items: end; }
.edit-grid { grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); align-items: end; margin-top: 10px; }
.field { display: grid; gap: 5px; color: #374151; font-weight: 700; min-width: 0; }
.field span { font-size: 12px; text-transform: uppercase; color: #6b7280; }
.native-input { min-height: 38px; width: 100%; min-width: 0; border: 1px solid #d7dde2; border-radius: 6px; padding: 8px 10px; background: #fff; color: #1f2937; }
.text-area { min-height: 70px; resize: vertical; }
.actions { display: flex; justify-content: flex-end; margin-top: 12px; }
.summary-row { grid-template-columns: repeat(4, minmax(0, 1fr)); margin-bottom: 16px; }
.summary-row > div { border: 1px solid #dce5e2; border-radius: 8px; padding: 12px; background: #f8fbfa; }
.summary-label { display: block; color: #6b7280; font-size: 12px; font-weight: 800; text-transform: uppercase; }
.summary-row strong { display: block; margin-top: 4px; color: #0b302c; font-size: 22px; }
.campaign-list { display: grid; gap: 14px; }
.campaign-row { display: grid; grid-template-columns: minmax(0, 1fr) 220px; gap: 16px; border-top: 1px solid #eef1f3; padding-top: 14px; }
.campaign-row:first-child { border-top: 0; padding-top: 0; }
.campaign-main, .campaign-actions { min-width: 0; }
.title-line { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.title-input { max-width: 360px; font-weight: 800; }
.status-badge { display: inline-flex; align-items: center; border-radius: 999px; padding: 4px 9px; font-size: 12px; font-weight: 800; white-space: nowrap; }
.status-badge.ok { background: #e8f5ec; color: #1f7a3b; }
.status-badge.info { background: #e7f2ff; color: #215b8f; }
.status-badge.draft { background: #fff3d6; color: #9a5b00; }
.status-badge.ended, .status-badge.muted { background: #eef0f2; color: #4b5563; }
.meta-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); margin-top: 10px; color: #4b5563; font-size: 13px; font-weight: 700; }
.approval-note { display: grid; gap: 3px; margin-top: 10px; padding: 10px; border-radius: 6px; background: #f8fafc; color: #374151; }
.history-line { margin-top: 8px; color: #6b7280; font-size: 13px; }
.campaign-actions { display: grid; align-content: start; gap: 10px; }
.approval-box, .manual-box { display: grid; gap: 8px; padding: 10px; border: 1px solid #e1e5e8; border-radius: 8px; background: #fbfcfd; }
.manual-box strong { color: #111827; font-size: 13px; }
.manual-box { overflow-wrap: anywhere; }
.audience-list { max-height: 180px; overflow-y: auto; padding-left: 18px; font-size: 12px; }
.grant-table-wrap { grid-column: 1 / -1; overflow-x: auto; border: 1px solid #e5e8eb; border-radius: 8px; }
.grant-table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 720px; }
.grant-table th, .grant-table td { text-align: left; padding: 9px 10px; border-top: 1px solid #edf0f2; vertical-align: top; overflow-wrap: anywhere; }
.grant-table th { color: #4b5563; background: #f8fafc; border-top: 0; }
.empty-state, .empty { color: #6b7280; }
.empty.compact { margin: 0; padding: 10px; }
@media (max-width: 1180px) {
    .form-grid, .edit-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .campaign-row { grid-template-columns: 1fr; }
    .campaign-actions { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 760px) {
    .page-head { flex-direction: column; }
    .form-grid, .edit-grid, .summary-row, .meta-grid, .campaign-actions { grid-template-columns: 1fr; }
    .title-line { align-items: flex-start; flex-direction: column; }
    .title-input { max-width: none; }
}
</style>
